import {Geometry} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/rasterize-geometry/definition"
import {toBase64} from "@cm/browser-utils"
import {Base64DataSchema, ImageProcessingNodes} from "@cm/image-processing-nodes"
import {Box2Like, Vector2, Vector2Like} from "@cm/math"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {z} from "zod"

export function convertToImageProcessingGeometry(geometry: Geometry): ImageProcessingNodes.Geometry {
    const positions: [number, number][] = []
    for (let i = 0; i < geometry.vertices.positions.length; i += 2) {
        positions.push([geometry.vertices.positions[i], geometry.vertices.positions[i + 1]])
    }
    let uvs: [number, number][] | undefined = undefined
    if (geometry.vertices.uvs) {
        uvs = []
        for (let i = 0; i < geometry.vertices.uvs.length; i += 2) {
            uvs.push([geometry.vertices.uvs[i], geometry.vertices.uvs[i + 1]])
        }
    }
    let colors: [number, number, number, number][] | undefined = undefined
    if (geometry.vertices.colors) {
        colors = []
        for (let i = 0; i < geometry.vertices.colors.length; i += 4) {
            colors.push([geometry.vertices.colors[i], geometry.vertices.colors[i + 1], geometry.vertices.colors[i + 2], geometry.vertices.colors[i + 3]])
        }
    }
    let indices: number[] | undefined = undefined
    if (geometry.indices) {
        indices = []
        for (let i = 0; i < geometry.indices.length; i++) {
            indices.push(geometry.indices[i])
        }
    }
    return {
        type: "geometry",
        topology: geometry.topology,
        vertices: {
            positions,
            uvs,
            colors,
        },
        indices,
    }
}

export async function uploadGeometry(uploadService: UploadGqlService, organizationId: string, geometry: Geometry, filename = "geometry.json") {
    const positions = geometry.vertices.positions
    const uvs = geometry.vertices.uvs
    const indices = geometry.indices
    const mapToBase64 = (data: Float32Array | Uint32Array): z.infer<typeof Base64DataSchema> => {
        return {
            type: "base64",
            data: toBase64(data),
        }
    }
    const geometryBase64: ImageProcessingNodes.GeometryBase64 = {
        type: "geometry-base64",
        topology: "triangleList",
        vertices: {
            positions: mapToBase64(positions),
            uvs: uvs ? mapToBase64(uvs) : undefined,
        },
        indices: indices ? mapToBase64(indices) : undefined,
    }
    const contentType = "application/json"
    const file = new File([new Blob([JSON.stringify(geometryBase64)], {type: contentType})], filename, {type: contentType})
    return uploadService.createAndUploadDataObject(file, {organizationId: organizationId}, {showUploadToolbar: false, processUpload: false})
}

const isPointInRect = (p: Vector2Like, b: Box2Like): boolean => {
    return p.x >= b.x && p.x <= b.x + b.width && p.y >= b.y && p.y <= b.y + b.height
}

const isPointInTriangle = (p: Vector2Like, a: Vector2Like, b: Vector2Like, c: Vector2Like): boolean => {
    const det = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y)
    const alpha = ((b.y - c.y) * (p.x - c.x) + (c.x - b.x) * (p.y - c.y)) / det
    const beta = ((c.y - a.y) * (p.x - c.x) + (a.x - c.x) * (p.y - c.y)) / det
    const gamma = 1 - alpha - beta
    return alpha >= 0 && beta >= 0 && gamma >= 0
}

const segmentsIntersect = (p1: Vector2Like, p2: Vector2Like, q1: Vector2Like, q2: Vector2): boolean => {
    const d1 = Vector2.cross({x: q2.x - q1.x, y: q2.y - q1.y}, {x: p1.x - q1.x, y: p1.y - q1.y})
    const d2 = Vector2.cross({x: q2.x - q1.x, y: q2.y - q1.y}, {x: p2.x - q1.x, y: p2.y - q1.y})
    const d3 = Vector2.cross({x: p2.x - p1.x, y: p2.y - p1.y}, {x: q1.x - p1.x, y: q1.y - p1.y})
    const d4 = Vector2.cross({x: p2.x - p1.x, y: p2.y - p1.y}, {x: q2.x - p1.x, y: q2.y - p1.y})
    return d1 * d2 < 0 && d3 * d4 < 0
}

export const triangleIntersectsRectangle = (p0: Vector2Like, p1: Vector2Like, p2: Vector2Like, b: Box2Like): boolean => {
    // Rectangle corners
    const rectCorners = [new Vector2(b.x, b.y), new Vector2(b.x + b.width, b.y), new Vector2(b.x + b.width, b.y + b.height), new Vector2(b.x, b.y + b.height)]

    // Step 1: Any triangle vertex inside the rectangle?
    if ([p0, p1, p2].some((p) => isPointInRect(p, b))) {
        return true
    }

    // Step 2: Any rectangle corner inside the triangle?
    if (rectCorners.some((corner) => isPointInTriangle(corner, p0, p1, p2))) {
        return true
    }

    // Step 3: Check edge intersections (triangle edges vs rectangle edges)
    const rectEdges = [
        [rectCorners[0], rectCorners[1]],
        [rectCorners[1], rectCorners[2]],
        [rectCorners[2], rectCorners[3]],
        [rectCorners[3], rectCorners[0]],
    ]

    const triangleEdges = [
        [p0, p1],
        [p1, p2],
        [p2, p0],
    ]

    for (const [pA, pB] of triangleEdges) {
        for (const [qA, qB] of rectEdges) {
            if (segmentsIntersect(pA, pB, qA, qB)) return true
        }
    }

    return false
}

// Clips a polygon against axis-aligned rectangle
export function clipPolygonToRect(pos: Vector2[], uv: Vector2[], rx1: number, ry1: number, rx2: number, ry2: number): {pos: Vector2; uv: Vector2}[] {
    let poly = pos.map((p, i) => ({pos: p, uv: uv[i]}))

    poly = clipAgainstEdge(
        poly,
        ({x}) => x >= rx1,
        (a, b) => intersectX(a, b, rx1),
    )
    poly = clipAgainstEdge(
        poly,
        ({x}) => x <= rx2,
        (a, b) => intersectX(a, b, rx2),
    )
    poly = clipAgainstEdge(
        poly,
        ({y}) => y >= ry1,
        (a, b) => intersectY(a, b, ry1),
    )
    poly = clipAgainstEdge(
        poly,
        ({y}) => y <= ry2,
        (a, b) => intersectY(a, b, ry2),
    )

    return poly
}

function clipAgainstEdge(
    poly: {pos: Vector2; uv: Vector2}[],
    inside: (p: Vector2) => boolean,
    intersect: (a: {pos: Vector2; uv: Vector2}, b: {pos: Vector2; uv: Vector2}) => {pos: Vector2; uv: Vector2},
) {
    const out: {pos: Vector2; uv: Vector2}[] = []
    for (let i = 0; i < poly.length; i++) {
        const curr = poly[i]
        const prev = poly[(i + poly.length - 1) % poly.length]
        const currInside = inside(curr.pos)
        const prevInside = inside(prev.pos)

        if (currInside !== prevInside) {
            out.push(intersect(prev, curr))
        }
        if (currInside) {
            out.push(curr)
        }
    }
    return out
}

function intersectX(a: {pos: Vector2; uv: Vector2}, b: {pos: Vector2; uv: Vector2}, x: number) {
    const t = (x - a.pos.x) / (b.pos.x - a.pos.x)
    return {
        pos: new Vector2(x, a.pos.y + t * (b.pos.y - a.pos.y)),
        uv: new Vector2(a.uv.x + t * (b.uv.x - a.uv.x), a.uv.y + t * (b.uv.y - a.uv.y)),
    }
}

function intersectY(a: {pos: Vector2; uv: Vector2}, b: {pos: Vector2; uv: Vector2}, y: number) {
    const t = (y - a.pos.y) / (b.pos.y - a.pos.y)
    return {
        pos: new Vector2(a.pos.x + t * (b.pos.x - a.pos.x), y),
        uv: new Vector2(a.uv.x + t * (b.uv.x - a.uv.x), a.uv.y + t * (b.uv.y - a.uv.y)),
    }
}
